'use client'

import React from 'react'
import { useI18n } from '../../../../../components/i18n'

const SUBSCRIPTION_CELEBRATION_MS = 3800
const SUBSCRIPTION_PARTICLES = Object.freeze([
  { x: '-34px', y: '-22px', delay: '40ms', size: '3px', rotate: '-148deg', color: '#ffe889' },
  { x: '-39px', y: '2px', delay: '150ms', size: '2px', rotate: '-178deg', color: '#79e8ff' },
  { x: '-29px', y: '27px', delay: '270ms', size: '3px', rotate: '142deg', color: '#ffc93f' },
  { x: '-8px', y: '-38px', delay: '90ms', size: '2px', rotate: '-102deg', color: '#ffffff' },
  { x: '13px', y: '-35px', delay: '330ms', size: '3px', rotate: '-68deg', color: '#56dfff' },
  { x: '30px', y: '-19px', delay: '210ms', size: '2px', rotate: '-32deg', color: '#ffe477' },
  { x: '34px', y: '5px', delay: '470ms', size: '3px', rotate: '8deg', color: '#ffffff' },
  { x: '24px', y: '28px', delay: '560ms', size: '2px', rotate: '48deg', color: '#ffc536' },
  { x: '1px', y: '39px', delay: '390ms', size: '3px', rotate: '88deg', color: '#67e7ff' },
  { x: '-20px', y: '34px', delay: '650ms', size: '2px', rotate: '122deg', color: '#fff2ad' },
  { x: '-43px', y: '-9px', delay: '590ms', size: '2px', rotate: '-166deg', color: '#ffc536' },
  { x: '21px', y: '-31px', delay: '720ms', size: '2px', rotate: '-54deg', color: '#fff8d6' },
])

export default function StarButton({ on, onClick, title = '', disabled = false, compact = false }) {
  const { t } = useI18n()
  const previousOnRef = React.useRef(!!on)
  const celebrationTimerRef = React.useRef(0)
  const [celebrating, setCelebrating] = React.useState(false)
  const successLabel = t?.('forum_subscription_success') || 'You subscribed'

  React.useEffect(() => {
    const wasOn = previousOnRef.current
    previousOnRef.current = !!on
    if (celebrationTimerRef.current) {
      window.clearTimeout(celebrationTimerRef.current)
      celebrationTimerRef.current = 0
    }
    if (!wasOn && on) {
      setCelebrating(true)
      celebrationTimerRef.current = window.setTimeout(() => {
        celebrationTimerRef.current = 0
        setCelebrating(false)
      }, SUBSCRIPTION_CELEBRATION_MS)
    } else if (!on) {
      setCelebrating(false)
    }
    return () => {
      if (!celebrationTimerRef.current) return
      window.clearTimeout(celebrationTimerRef.current)
      celebrationTimerRef.current = 0
    }
  }, [on])

  const stopBubble = (e) => {
    e.stopPropagation()
  }

  return (
    <button
      type="button"
      className={`starBtn ${on ? 'on' : 'off'} ${disabled ? 'dis' : ''} ${compact ? 'starBtn--compact' : ''} ${celebrating ? 'isSubscriptionCelebrating' : ''}`}
      title={title}
      aria-label={title || undefined}
      disabled={!!disabled}
      onPointerDownCapture={stopBubble}
      onMouseDownCapture={stopBubble}
      onTouchStartCapture={stopBubble}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        if (!disabled) onClick?.(e)
      }}
      data-no-thread-open="1"
      data-subscription-celebrating={celebrating ? '1' : undefined}
      aria-pressed={!!on}
      aria-disabled={disabled}
    >
      <span className="starBtnGlyph" aria-hidden="true">
        <svg viewBox="0 0 24 24" width={compact ? 11 : 16} height={compact ? 11 : 16}>
          <path
            d="M12 17.3l-5.4 3.2 1.5-6.2-4.9-4.1 6.4-.5L12 3.8l2.4 5.9 6.4.5-4.9 4.1 1.5 6.2L12 17.3Z"
            className="starPath"
          />
        </svg>
        <span className="starBtnAura" aria-hidden="true" />
        <span className="starBtnSunGlint" aria-hidden="true" data-brand-logo-glint="1" />
        <span className="starBtnNova" aria-hidden="true" />
        <span className="starBtnParticles" aria-hidden="true">
          {SUBSCRIPTION_PARTICLES.map((particle, index) => (
            <i
              key={`${particle.x}:${particle.y}`}
              data-star-particle={index + 1}
              style={{
                '--star-particle-x': particle.x,
                '--star-particle-y': particle.y,
                '--star-particle-delay': particle.delay,
                '--star-particle-size': particle.size,
                '--star-particle-rotate': particle.rotate,
                '--star-particle-color': particle.color,
              }}
            />
          ))}
        </span>
      </span>
      <span className="starBtnSuccess" aria-hidden={!celebrating}>{successLabel}</span>
      <span className="starBtnAnnouncement" role="status" aria-live="polite" aria-atomic="true">
        {celebrating ? successLabel : ''}
      </span>
    </button>
  )
}
